import { useEffect, useMemo, useState } from "react";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import RadioGroup from "@enerex/unified-ui-react/form/RadioGroup";
import ErrorIcon from "@enerex/unified-ui-react/icons/ErrorIcon";
import FormInput from "components/FormInput/index";
import Select from "components/UnifiedSelect";
import { CommodityIcon } from "pages/utilities/CommodityIcons";
import {
  createSupplierRateClass,
  getCommodities,
  getMasterRateClasses,
  getUtilityRateClasses,
} from "pages/rateClass/list/rateClassApi";

const getCommodityType = (option) => {
  const id = Number(option?.commodityId);
  if (id === 1) return "electric";
  if (id === 2) return "gas";

  const commodity = String(option?.commodity || option?.label || "").trim().toLowerCase();
  if (commodity === "electric" || commodity === "electricity") return "electric";
  if (commodity === "gas" || commodity === "natural gas") return "gas";

  return null;
};

const styles = {
  modalBody: {
    maxHeight: "60vh",
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: 6,
  },
};

const AddSupplierRateClassModal = ({ show, company, onHide, onSaved }) => {
  const [masterRateClass, setMasterRateClass] = useState(null);
  const [commodity, setCommodity] = useState(null);
  const [utility, setUtility] = useState(null);
  const [rateClassName, setRateClassName] = useState("");
  const [error, setError] = useState("");
  const [masterRateClassOptions, setMasterRateClassOptions] = useState([]);
  const [commodityOptions, setCommodityOptions] = useState([]);
  const [utilityRows, setUtilityRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const utilityOptions = useMemo(() => utilityRows
    .filter((item) => !commodity?.commodityId || Number(item.commodityId) === Number(commodity.commodityId))
    .map((item) => ({
      value: item.utilityRateClassId || item.utilityId,
      label: item.utilityName || "",
      utilityId: item.utilityId,
      utilityName: item.utilityName || "",
      utilityRateClassId: item.utilityRateClassId,
      commodityId: item.commodityId,
      commodity: item.commodity,
    }))
    .filter((item) => item.value && item.label), [utilityRows, commodity]);

  const commodityRadioOptions = useMemo(() => commodityOptions.map((option) => {
    const type = getCommodityType(option);

    return {
      id: `company-supplier-rate-class-commodity-${option.value}`,
      value: option.value,
      label: (
        <span className="d-inline-flex align-items-center gap-1">
          {type ? <CommodityIcon type={type} /> : null}
          <span>{option.label}</span>
        </span>
      ),
    };
  }), [commodityOptions]);

  useEffect(() => {
    if (!show) return;
    setMasterRateClass(null);
    setCommodity(null);
    setUtility(null);
    setRateClassName("");
    setError("");
    setUtilityRows([]);
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const loadOptions = async () => {
      setLoading(true);
      try {
        const [masterClasses, commodities] = await Promise.all([getMasterRateClasses(), getCommodities()]);
        setMasterRateClassOptions(masterClasses.map((item) => ({
          value: item.rateClassId,
          label: item.rateClassName,
          ...item,
        })).filter((item) => item.value && item.label));
        setCommodityOptions(commodities);
      } catch (loadError) {
        setError(loadError.message || "Failed to load rate class options.");
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, [show]);

  useEffect(() => {
    if (!show || !masterRateClass?.rateClassId) return;

    const loadUtilities = async () => {
      setLoading(true);
      try {
        setUtilityRows(await getUtilityRateClasses(masterRateClass.rateClassId));
      } catch (loadError) {
        setError(loadError.message || "Failed to load utility rate classes.");
      } finally {
        setLoading(false);
      }
    };

    setUtility(null);
    loadUtilities();
  }, [show, masterRateClass]);

  const handleCommodityChange = (option) => {
    setCommodity(option);
    setUtility(null);
  };

  const handleSave = async () => {
    setError("");

    const supplierId = company?.enerexCompanyId || company?.EnerexCompanyId;
    const supplierName = company?.legalName || company?.LegalName || "";

    if (!supplierId) {
      setError("Missing Enerex company id for this supplier.");
      return;
    }
    if (!masterRateClass?.rateClassId) {
      setError("Please select a master rate class.");
      return;
    }
    if (!commodity?.commodityId) {
      setError("Please select a commodity.");
      return;
    }
    if (!utility?.utilityRateClassId) {
      setError("Please select a utility.");
      return;
    }
    if (!rateClassName.trim()) {
      setError("Please enter supplier rate class.");
      return;
    }

    setSaving(true);
    try {
      await createSupplierRateClass({
        rateClassId: masterRateClass.rateClassId,
        commodityId: utility.commodityId,
        commodity: utility.commodity,
        utilityId: utility.utilityId,
        utilityName: utility.utilityName,
        utilityRateClassId: utility.utilityRateClassId,
        supplierId,
        supplierName,
        rateClassName: rateClassName.trim(),
      });
      await onSaved?.();
      onHide();
    } catch (saveError) {
      setError(saveError.message || "Failed to create supplier rate class.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmationModal
      centered
      dialogClassName="modal-md"
      show={show}
      onHide={onHide}
      title="Add Supplier Rate Class"
      confirmButtonLabel="Save"
      confirmButtonVariant="primary"
      isLoading={saving}
      onConfirm={handleSave}
    >
      <div style={styles.modalBody}>
        {error ? (
          <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
            <ErrorIcon />
            <span>{error}</span>
          </div>
        ) : null}
        <div className="mb-3">
          <RadioGroup
            inline
            name="company-supplier-rate-class-commodity"
            options={commodityRadioOptions}
            value={commodity?.value || null}
            onChange={(value) => {
              const selectedCommodity = commodityOptions.find((option) => String(option.value) === String(value));
              if (selectedCommodity) handleCommodityChange(selectedCommodity);
            }}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Rate Class</label>
          <Select value={masterRateClass} options={masterRateClassOptions} onChange={setMasterRateClass} placeholder="Select rate class" isLoading={loading} classNamePrefix="react-select" />
        </div>
        <div className="mb-3">
          <label className="form-label">Utility</label>
          <Select value={utility} options={utilityOptions} onChange={setUtility} placeholder="Select utility" isDisabled={!masterRateClass || !commodity} classNamePrefix="react-select" />
        </div>
        <FormInput
          type="text"
          label="Supplier Rate Class"
          containerClass="mb-3"
          value={rateClassName}
          onChange={(event) => setRateClassName(event.target.value)}
          placeholder="Enter supplier rate class"
        />
      </div>
    </ConfirmationModal>
  );
};

export default AddSupplierRateClassModal;
